import * as React from 'react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useApprovePO, useRejectPO } from '@/hooks/usePOMutations'
import { usePO } from '@/hooks/usePOs'
import type { components } from '@/lib/api/generated'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/lib/dates'
import { useUIStore } from '@/store/uiStore'

type POResponse = components['schemas']['POResponse']

function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(val)
}

function POSummary({ po }: { po: POResponse }) {
  const items = po.items ?? []
  const subtotal = items.reduce((s, it) => s + it.cost_base * it.qty_ordered, 0)
  const units = items.reduce((s, it) => s + it.qty_ordered, 0)

  const supplier = (po.supplier ?? '').trim()
  const vendor = (po.vendor ?? '').trim()

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header card */}
      <div className="border-b border-[var(--line-warm)] bg-[var(--surface-page)] px-5 py-3.5">
        <div className="flex items-baseline gap-3">
          <span className="font-semibold text-[var(--text-primary)]">
            {po.po_number ? `#${po.po_number}` : po.po_id}
          </span>
          <span className="text-xs text-[var(--mute)]">·</span>
          <div className="flex flex-col leading-tight">
            <span className="text-sm text-[var(--text-primary)]">{supplier || vendor}</span>
            {supplier && vendor && supplier !== vendor && (
              <span className="text-xs text-[var(--mute)]">{vendor}</span>
            )}
          </div>
          <span className="text-xs text-[var(--mute)]">·</span>
          <span className="text-xs text-[var(--mute)]">{po.location_to_receive}</span>
          {po.created_date && (
            <>
              <span className="text-xs text-[var(--mute)]">·</span>
              <span className="text-xs text-[var(--mute)]">
                Created {fmtDate(po.created_date, '—')}{po.created_by ? ` by ${po.created_by}` : ''}
              </span>
            </>
          )}
        </div>
        {po.notes && (
          <div className="mt-1.5 text-xs text-[var(--mute)] italic">{po.notes}</div>
        )}
        {/* Server-managed approval-notes thread (one appended line per
            submit/resubmit/approve note) — this is what the approver reviews. */}
        {(po.approval_notes ?? '').trim() && (
          <div className="mt-1.5" data-testid="approval-notes-thread">
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--text-primary)]">
              Approval notes
            </div>
            <div className="whitespace-pre-line text-xs leading-[1.6] text-[var(--text-primary)]">
              {po.approval_notes}
            </div>
          </div>
        )}
      </div>

      {/* Items table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-header)] sticky top-0">
            <tr>
              <th className="tbl-th text-left">Item</th>
              <th className="tbl-th w-[64px] text-right">Qty</th>
              <th className="tbl-th w-[90px] text-right">Unit cost</th>
              <th className="tbl-th w-[96px] text-right">Line</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.po_item_id} className="border-b border-[var(--line-warm)]">
                <td className="px-4 py-2">
                  <div className="font-medium text-[var(--text-primary)]">{it.product_title}</div>
                  <div className="text-xs text-[var(--mute)]">
                    {it.variant_title || '—'} · {it.barcode || it.sku || '—'}
                    {it.vendor ? ` · ${it.vendor}` : ''}
                  </div>
                  {/* Per-line item note (e.g. "Jane Doe wants this one"). */}
                  {(it.notes ?? '').trim() && (
                    <div className="text-xs italic text-[var(--mute)]">{it.notes}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{it.qty_ordered}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[var(--mute)]">{formatCurrency(it.cost_base)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(it.cost_base * it.qty_ordered)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals footer */}
      <div className="flex items-center justify-end gap-4 border-t border-[var(--line-warm)] bg-[var(--surface-page)] px-5 py-2.5 text-sm">
        <span className="text-[var(--mute)]">
          {items.length} SKU{items.length !== 1 ? 's' : ''} · {units} units
        </span>
        <span className="font-semibold">
          {formatCurrency(subtotal)}
        </span>
      </div>
    </div>
  )
}

function RejectForm({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (reason: string) => void
  onCancel: () => void
  busy: boolean
}) {
  const [reason, setReason] = useState('')
  const invalid = reason.trim().length === 0

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--line-warm)] bg-[var(--surface-page)] px-5 py-3.5">
      <div className="text-sm font-semibold text-[var(--text-primary)]">Reject this PO</div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="rejection-reason" className="text-xs">
          Reason <span className="text-[var(--status-rejected-fg)]">*</span>
        </Label>
        <Input
          id="rejection-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this PO is being rejected…"
          disabled={busy}
          className={cn(invalid && reason.length > 0 ? 'border-[var(--status-rejected-fg)]' : '')}
          data-testid="rejection-reason-input"
          autoFocus
        />
        {invalid && reason.length > 0 && (
          <p className="text-xs text-[var(--status-rejected-fg)]">Reason is required</p>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={busy || invalid}
          onClick={() => onConfirm(reason.trim())}
          data-testid="confirm-reject-button"
        >
          {busy ? 'Rejecting…' : 'Confirm rejection'}
        </Button>
      </div>
    </div>
  )
}

export function ApprovalModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const activePO = useUIStore((s) => s.activePO)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'approval'

  const [approvalNotes, setApprovalNotes] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  const { data: po, isLoading } = usePO(open ? activePO : null)
  const approveMutation = useApprovePO()
  const rejectMutation = useRejectPO()
  const busy = approveMutation.isPending || rejectMutation.isPending

  React.useEffect(() => {
    if (open) {
      setApprovalNotes('')
      setShowRejectForm(false)
      approveMutation.reset()
      rejectMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function close() {
    setActiveModal(null)
  }

  async function handleApprove() {
    if (!activePO) return
    try {
      await approveMutation.mutateAsync({
        po_id: activePO,
        notes: approvalNotes.trim() || undefined,
      })
      const label = po?.po_number ? `#${po.po_number}` : activePO
      toast.success(`PO ${label} approved`)
      close()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve PO')
    }
  }

  async function handleReject(reason: string) {
    if (!activePO) return
    try {
      await rejectMutation.mutateAsync({ po_id: activePO, reason })
      const label = po?.po_number ? `#${po.po_number}` : activePO
      toast.success(`PO ${label} rejected`)
      close()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject PO')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && close()}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex h-[80vh] w-[80vw] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
        showCloseButton={false}
      >
        <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3.5 sm:text-left">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-semibold">
              Review PO for approval
            </DialogTitle>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={close}
              disabled={busy}
              aria-label="Close"
            >
              ✕
            </Button>
          </div>
          <DialogDescription className="sr-only">
            Review the purchase order and approve or reject it.
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {isLoading && (
            <div className="flex flex-1 items-center justify-center text-sm text-[var(--mute)]">
              Loading PO…
            </div>
          )}
          {!isLoading && !po && (
            <div className="flex flex-1 items-center justify-center text-sm text-[var(--status-rejected-fg)]">
              Failed to load PO
            </div>
          )}
          {po && <POSummary po={po} />}
        </div>

        {/* Reject form (shown inline when user clicks Reject) */}
        {showRejectForm && (
          <RejectForm
            onConfirm={handleReject}
            onCancel={() => setShowRejectForm(false)}
            busy={rejectMutation.isPending}
          />
        )}

        {/* Footer */}
        {!showRejectForm && (
          <div className="flex items-center gap-2 border-t border-[var(--line-warm)] bg-[var(--surface-card)] px-5 py-3">
            <div className="flex flex-col gap-1 flex-1">
              <Label htmlFor="approval-notes" className="text-xs text-[var(--mute)]">
                Notes (optional)
              </Label>
              <Input
                id="approval-notes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Any notes for this approval…"
                disabled={busy}
                className="h-8 text-xs w-64"
                data-testid="approval-notes-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={close}
              disabled={busy}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setShowRejectForm(true)}
              disabled={busy}
              data-testid="reject-button"
            >
              Reject
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleApprove}
              disabled={busy || !po}
              data-testid="approve-button"
            >
              {approveMutation.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
